/**
 * New Document Upload modal for Candid auth document uploads.
 *
 * Reads the dialog title from Redux modal metaData. Order identifiers come
 * from `generalOrderDetails`, with optional metaData seeds (`orderId`, `orgId`,
 * `mrn`) taking precedence when present.
 * clear Redux `openedModalId` via `onClose` so the dialog unmounts instead of
 * remaining as a closed `<dialog>` at the bottom of the page.
 *
 * Save stays disabled until {@link CandidDocumentUploadFormState.isValid}
 * — a non-blank document name and at least one finished file upload.
 */
import { Modal, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import {
  CandidDocumentUploadFormState,
  EMPTY_CANDID_DOCUMENT_UPLOAD_FORM_STATE,
} from "../../../data-model/candidDocumentUploadFormState";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { useCandidDocumentUploadSave } from "./hooks/useCandidDocumentUploadSave";
import NewDocUploadBody from "./newDocUploadBody";

/**
 * Shape expected on `state.modal.metaData` when this modal is opened.
 */
export interface NewDocumentUploadMetaData {
  /** Dialog title; falls back to "New Document Upload". */
  title?: string;
  /** PA order identifier for the document POST. */
  orderId?: string;
  /** Organisation identifier for the document POST. */
  orgId?: string;
  /** Patient MRN from `generalOrderDetails.demographics`. */
  mrn?: string;
}

interface NewDocumentUploadProps {
  /**
   * Invoked when the dialog is dismissed so the parent can clear Redux modal
   * state.
   */
  onClose: () => void;
}

/**
 * Renders the New Document Upload dialog.
 *
 * @param props.onClose - Clears Redux modal state and unmounts this dialog
 * @returns The modal element
 */
const NewDocumentUpload = ({ onClose }: NewDocumentUploadProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metaData } = useSelector((state: RootState) => state.modal);
  const newDocumentUploadMetaData = metaData as
    NewDocumentUploadMetaData | undefined;
  const { generalOrderDetails, fetchAll } = useDataAccumulatorForSingleOrder();
  const [formState, setFormState] = useState<CandidDocumentUploadFormState>(
    EMPTY_CANDID_DOCUMENT_UPLOAD_FORM_STATE,
  );

  const uploadContext = useMemo(
    () => ({
      orderId: generalOrderDetails?.order?.order_id ?? "",
      orgId: getOrgIdForFetchExternalWorklist() ?? "",
      mrn:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
    }),
    [newDocumentUploadMetaData, generalOrderDetails],
  );

  /**
   * Refetches order data, closes the dialog, and toasts after a successful
   * document POST.
   */
  const onUploadSuccess = useCallback(async () => {
    await fetchAll(
      generalOrderDetails?.order?.order_id ?? "",
      getOrgIdForFetchExternalWorklist() ?? "",
      { force: true },
    );
    dispatch(closeModal());
    toast.success(ToastMessages.CANDID_DOCUMENT_UPLOAD_INITIATED);
  }, [fetchAll, generalOrderDetails, dispatch]);

  const { handleSave, isSaveDisabled, saveButtonText } =
    useCandidDocumentUploadSave({
      formState,
      context: uploadContext,
      onSuccess: onUploadSuccess,
    });

  return (
    <Modal
      dialogId={ModalId.NEW_DOCUMENT_UPLOAD_MODAL}
      onSave={handleSave}
      onClose={onClose}
      onCancel={onClose}
      disableSave={isSaveDisabled}
      saveButtonText={saveButtonText}
      cancelText={"Cancel"}
      maxHeightPercentage={50}
      title={newDocumentUploadMetaData?.title ?? "New Document Upload"}
    >
      <div className="flex flex-col gap-2">
        <NewDocUploadBody onChange={setFormState} />
      </div>
    </Modal>
  );
};

export default NewDocumentUpload;
